import React, {
    createContext,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";
import {
    IEventModule,
    IHandleLogin,
} from "@Modules/eventPage/root/IEventModule";
import { getIsRenewBooking } from "@Modules/eventPage/components/RenewBookingForm/utils/getIsRenewBooking";
import {
    isEmpty,
    SELECTORS,
    useEventDataOfSelectedEvent,
    useFormDetails,
} from "@Utils";
import {
    EXTRA_FIELD,
    FORM_ENUM,
    FORM_FIELDS as USER_DETAILS_FORM_FIELDS,
} from "@Modules/modals/components/UserDetails/FormData";
import { getShowGstOption } from "@Modules/eventPage/utils/getShowGstOption";
import { useSelector } from "react-redux";
import { IHost, IUser } from "@Interfaces";
import { getBillingStateNonMandatory } from "@Modules/eventPage/utils/getBillingStateNonMandatory";

type IRenewBookingFormContextValue = {
    isRenewBooking: boolean;
    rebookingFormRef?: React.RefObject<HTMLDivElement>;
    renewBookingFormDetails: {
        [field: string]: IUser.IFormDataVal;
    };
    setRenewBookingFormDetails: (
        p: void | IUser.IFormDataVal | IUser.IFormDataVal[] | undefined
    ) => void;
    showGstOption: boolean;
    isGstChecked: boolean;
    handleGstCheck: () => void;
    renewBookingFormFields: string[];
    renewBookingBaseFormFields: string[];
    renewBookingGstFormFields: string[];
    billingStateNonMandatory: boolean;
    isRebookingFomrValid: boolean;
    handleRenewBookingCtaClick: (handleLogin: IHandleLogin) => void;
};

const initialContextValue: IRenewBookingFormContextValue = {
    isRenewBooking: false,
    renewBookingFormDetails: {},
    setRenewBookingFormDetails: () => {},
    showGstOption: false,
    isGstChecked: false,
    handleGstCheck: () => {},
    renewBookingFormFields: [],
    renewBookingBaseFormFields: [],
    renewBookingGstFormFields: [],
    billingStateNonMandatory: true,
    isRebookingFomrValid: false,
    handleRenewBookingCtaClick: () => {},
};

const RenewBookingFormContext = createContext(initialContextValue);

export function RebookingFormContextProvider({
    children,
    urlQueryParams,
}: {
    children: React.ReactNode;
    urlQueryParams: IEventModule.IProps["urlQueryParams"];
}) {
    const host = useSelector(SELECTORS.host);
    const { event, eventData } = useEventDataOfSelectedEvent();
    const isRenewBooking = getIsRenewBooking({ urlQueryParams, eventData });

    const showGstOption = getShowGstOption({ event, eventData, host });

    const renewBookingBaseFormFields = [
        USER_DETAILS_FORM_FIELDS.email,
        ...(showGstOption ? [EXTRA_FIELD.billing_state] : []),
    ];

    const [isGstChecked, setIsGstChecked] = useState(false); // Whether the user has opted to fill GST details
    const handleGstCheck = () => {
        setIsGstChecked(!isGstChecked);
    };

    const eInvoiceEnabled = host?.dataHost?.profile_data?.einvoice_enabled;

    const renewBookingGstFormFields: Array<string> = [];
    if (showGstOption && isGstChecked) {
        renewBookingGstFormFields.push(EXTRA_FIELD.gst_info_gst_name);
        renewBookingGstFormFields.push(EXTRA_FIELD.gst_info_gst_number);
        if (eInvoiceEnabled) {
            renewBookingGstFormFields.push(EXTRA_FIELD.gst_info_address);
            renewBookingGstFormFields.push(EXTRA_FIELD.gst_info_pincode);
        }
    }

    const renewBookingFormFields = [
        ...renewBookingBaseFormFields,
        ...renewBookingGstFormFields,
    ];

    const {
        formDetails: renewBookingFormDetails,
        setFormDetails: setRenewBookingFormDetails,
        valid: isRebookingFomrValid,
    } = useFormDetails(renewBookingFormFields, "renewBookingForm");

    const hostData = host?.dataHost?.profile_data || ({} as IHost.IHostData);
    const billingStateNonMandatory = getBillingStateNonMandatory({
        event,
        eventData,
        hostData,
    });

    useEffect(() => {
        setRenewBookingFormDetails([
            ...[...renewBookingGstFormFields, EXTRA_FIELD.billing_state].map(
                f => {
                    const field = renewBookingFormDetails?.[f] || {};
                    const value = field.value ?? "";

                    return {
                        key: f,
                        value,
                        showError: field.showError ?? false,
                        error:
                            FORM_ENUM?.[f]?.validate?.(value, {
                                billingStateNonMandatory,
                                isGstChecked,
                            }) ?? null,
                    };
                }
            ),
        ]);
    }, [isGstChecked]);

    const rebookingFormRef = useRef<HTMLDivElement>(null);

    const onErrorInRebookingForm = () => {
        setRenewBookingFormDetails(
            Object.values(renewBookingFormFields).map((f: string) => ({
                key: f,
                showError: true,
            }))
        );
        alert("Please enter valid details");
        rebookingFormRef?.current?.scrollIntoView({
            behavior: "smooth",
        });
    };

    const handleRenewBookingCtaClick = (handleLogin: IHandleLogin) => {
        if (isRebookingFomrValid) {
            const finalFormData: Record<string | number | symbol, string> =
                Object.assign(
                    {},
                    ...Object.values(renewBookingFormDetails).map(v => {
                        if (
                            !(isGstChecked || !billingStateNonMandatory) &&
                            EXTRA_FIELD.billing_state === v.key &&
                            isEmpty((v.value || "").trim())
                        ) {
                            // in case billing_state is not set, backend assigns the default value
                            // so dont send this key
                            return {};
                        }
                        return {
                            [v.key]: (v.value || "").trim(),
                        };
                    })
                );

            handleLogin({ customerDetailsProp: [finalFormData] });
        } else {
            onErrorInRebookingForm();
        }
    };

    const contextValue: IRenewBookingFormContextValue = {
        isRenewBooking,
        rebookingFormRef,
        renewBookingFormDetails,
        setRenewBookingFormDetails,
        showGstOption,
        isGstChecked,
        handleGstCheck,
        renewBookingFormFields,
        renewBookingBaseFormFields,
        renewBookingGstFormFields,
        billingStateNonMandatory,
        isRebookingFomrValid,
        handleRenewBookingCtaClick,
    };

    return (
        <RenewBookingFormContext.Provider value={contextValue}>
            {children}
        </RenewBookingFormContext.Provider>
    );
}

export function useRenewBookingFormContext() {
    return useContext(RenewBookingFormContext);
}
